import { logger } from './logger'

interface RetryOptions {
  maxAttempts: number
  delayMs: number
  backoffMultiplier: number
}

const RETRY_DEFAULTS: RetryOptions = {
  maxAttempts: 3,
  delayMs: 500,
  backoffMultiplier: 2,
}

/**
 * Executes a provided asynchronous operation with retry logic, applying an exponential backoff
 * strategy between retries and stopping after a specified number of attempts.
 *
 * Example (delayMs = 500, backoffMultiplier = 2, maxAttempts = 3):
 * 1. Try
 * 2. Retry in 500 * 2^0 = 500 ms
 * 3. Retry in 500 * 2^1 = 1000 ms
 * 4. Retry in 500 * 2^2 = 2000 ms
 * 5. No more retry, maxAttempts reached.
 */
export async function withRetry<T>(operation: () => Promise<T>, options: RetryOptions = RETRY_DEFAULTS): Promise<T> {
  const { maxAttempts, delayMs, backoffMultiplier } = options

  let lastError: unknown
  for (let attempt = 0; attempt < maxAttempts; ++attempt) {
    try {
      return await operation()
    } catch (error) {
      lastError = error
      logger.info(`Attempt ${attempt + 1} failed:`)
      // Timeout before retrying
      if (attempt < maxAttempts) {
        const delay = delayMs * backoffMultiplier ** attempt
        logger.info(`Retrying in ${delay} ms...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError
}
